// src/pages/finance-portal/per-diem/PerDiem.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton, Tooltip, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox, InputAdornment,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const CLAIM_STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED: { bg: '#dbeafe', color: '#1e40af' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
    PAID: { bg: '#e0e7ff', color: '#3730a3' },
};

export default function PerDiem() {
    const [tabValue, setTabValue] = useState(0);

    // === RATES TAB ===
    const [rates, setRates] = useState([]);
    const [ratesLoading, setRatesLoading] = useState(true);
    const [rateDialog, setRateDialog] = useState({ open: false, editing: null });
    const [rateForm, setRateForm] = useState({ city: '', state: '', lodging_rate: '', meals_rate: '', incidentals_rate: '' });
    const [rateSaving, setRateSaving] = useState(false);

    // === CLAIMS TAB ===
    const [claims, setClaims] = useState([]);
    const [claimsLoading, setClaimsLoading] = useState(true);
    const [claimDialog, setClaimDialog] = useState({ open: false });
    const [claimForm, setClaimForm] = useState({ travel_date: '', city: '', state: '', is_first_day: false, is_last_day: false });
    const [claimSaving, setClaimSaving] = useState(false);

    // === CALCULATOR TAB ===
    const [calcForm, setCalcForm] = useState({ city: '', state: '', start_date: '', end_date: '' });
    const [calcResult, setCalcResult] = useState(null);
    const [calculating, setCalculating] = useState(false);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch rates
    const fetchRates = useCallback(async () => {
        setRatesLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/per-diem/rates');
            if (res.data.success) setRates(res.data.data || []);
        } catch (err) { setError(err.response?.data?.message || 'Failed to load rates'); }
        finally { setRatesLoading(false); }
    }, []);

    // Fetch claims
    const fetchClaims = useCallback(async () => {
        setClaimsLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/per-diem/claims');
            if (res.data.success) setClaims(res.data.data || []);
        } catch (err) { setError(err.response?.data?.message || 'Failed to load claims'); }
        finally { setClaimsLoading(false); }
    }, []);

    useEffect(() => {
        if (tabValue === 0) fetchRates();
        else if (tabValue === 1) fetchClaims();
    }, [tabValue, fetchRates, fetchClaims]);

    // Rate CRUD
    const openRateDialog = (rate = null) => {
        if (rate) {
            setRateForm({
                city: rate.city || '',
                state: rate.state || '',
                lodging_rate: rate.lodging_rate || '',
                meals_rate: rate.meals_rate || '',
                incidentals_rate: rate.incidentals_rate || '',
            });
        } else {
            setRateForm({ city: '', state: '', lodging_rate: '', meals_rate: '', incidentals_rate: '' });
        }
        setRateDialog({ open: true, editing: rate });
    };

    const saveRate = async () => {
        setRateSaving(true);
        const payload = {
            city: rateForm.city,
            state: rateForm.state,
            lodging_rate: parseFloat(rateForm.lodging_rate) || 0,
            meals_rate: parseFloat(rateForm.meals_rate) || 0,
            incidentals_rate: parseFloat(rateForm.incidentals_rate) || 0,
        };
        try {
            if (rateDialog.editing) {
                await axiosInstance.put(`/api/finance/per-diem/rates/${rateDialog.editing.id}`, payload);
                setSuccess('Rate updated');
            } else {
                await axiosInstance.post('/api/finance/per-diem/rates', payload);
                setSuccess('Rate created');
            }
            setRateDialog({ open: false, editing: null });
            fetchRates();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save rate');
        } finally {
            setRateSaving(false);
        }
    };

    const deleteRate = async (id) => {
        try {
            await axiosInstance.delete(`/api/finance/per-diem/rates/${id}`);
            setSuccess('Rate deleted');
            fetchRates();
        } catch (err) { setError(err.response?.data?.message || 'Failed to delete rate'); }
    };

    // Claims
    const saveClaim = async () => {
        setClaimSaving(true);
        try {
            await axiosInstance.post('/api/finance/per-diem/claims', {
                travel_date: claimForm.travel_date,
                city: claimForm.city,
                state: claimForm.state,
                is_first_day: claimForm.is_first_day,
                is_last_day: claimForm.is_last_day,
            });
            setSuccess('Claim created');
            setClaimDialog({ open: false });
            setClaimForm({ travel_date: '', city: '', state: '', is_first_day: false, is_last_day: false });
            fetchClaims();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create claim');
        } finally {
            setClaimSaving(false);
        }
    };

    // Calculator
    const handleCalculate = async () => {
        setCalculating(true);
        setCalcResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/per-diem/calculate', {
                city: calcForm.city,
                state: calcForm.state,
                start_date: calcForm.start_date,
                end_date: calcForm.end_date,
            });
            if (res.data.success) {
                setCalcResult(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Calculation failed');
        } finally {
            setCalculating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FlightTakeoffIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Per Diem</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Travel rates, claims & calculator</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Rates" />
                    <Tab label="Claims" />
                    <Tab label="Calculator" />
                </Tabs>

                {/* ========== RATES TAB ========== */}
                {tabValue === 0 && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openRateDialog()}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                Add Rate
                            </Button>
                        </Box>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['City', 'State', 'Lodging', 'Meals', 'Incidentals', 'Total', 'Actions'].map(h => (
                                                <TableCell key={h} sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                                                    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                                                    textAlign: ['Lodging', 'Meals', 'Incidentals', 'Total'].includes(h) ? 'right' : 'left',
                                                }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ratesLoading ? (
                                            [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                        ) : rates.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No per diem rates configured</Typography>
                                            </TableCell></TableRow>
                                        ) : rates.map(r => {
                                            const total = (parseFloat(r.lodging_rate) || 0) + (parseFloat(r.meals_rate) || 0) + (parseFloat(r.incidentals_rate) || 0);
                                            return (
                                                <TableRow key={r.id} hover>
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{r.city}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{r.state}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(r.lodging_rate)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(r.meals_rate)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(r.incidentals_rate)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: ACCENT, textAlign: 'right' }}>{formatCurrency(total)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openRateDialog(r)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteRate(r.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </>
                )}

                {/* ========== CLAIMS TAB ========== */}
                {tabValue === 1 && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setClaimDialog({ open: true })}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                New Claim
                            </Button>
                        </Box>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['ID', 'Travel Date', 'City', 'State', 'Lodging', 'Meals', 'Incidentals', 'Total', 'First/Last', 'Status'].map(h => (
                                                <TableCell key={h} sx={{
                                                    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                                                    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                                                    textAlign: ['Lodging', 'Meals', 'Incidentals', 'Total'].includes(h) ? 'right' : 'left',
                                                }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {claimsLoading ? (
                                            [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={10}><Skeleton height={36} /></TableCell></TableRow>)
                                        ) : claims.length === 0 ? (
                                            <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No per diem claims</Typography>
                                            </TableCell></TableRow>
                                        ) : claims.map(c => {
                                            const statusStyle = CLAIM_STATUS_COLORS[c.status] || { bg: '#f1f5f9', color: '#64748b' };
                                            const total = (parseFloat(c.lodging_amount) || 0) + (parseFloat(c.meals_amount) || 0) + (parseFloat(c.incidentals_amount) || 0);
                                            return (
                                                <TableRow key={c.id} hover>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>#{c.id}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(c.travel_date)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{c.city}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{c.state}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(c.lodging_amount)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(c.meals_amount)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(c.incidentals_amount)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: ACCENT, textAlign: 'right' }}>{formatCurrency(c.total_amount || total)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            {c.is_first_day && <Chip label="First" size="small" sx={{ fontFamily, fontSize: '0.6rem', height: 18, bgcolor: '#dbeafe', color: '#1e40af' }} />}
                                                            {c.is_last_day && <Chip label="Last" size="small" sx={{ fontFamily, fontSize: '0.6rem', height: 18, bgcolor: '#fef3c7', color: '#92400e' }} />}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={c.status} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </>
                )}

                {/* ========== CALCULATOR TAB ========== */}
                {tabValue === 2 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Per Diem Calculator</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <TextField label="City" value={calcForm.city}
                                onChange={e => setCalcForm(prev => ({ ...prev, city: e.target.value }))}
                                size="small" sx={{ minWidth: 180 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                            <TextField label="State" value={calcForm.state}
                                onChange={e => setCalcForm(prev => ({ ...prev, state: e.target.value }))}
                                size="small" sx={{ width: 120 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                            <TextField label="Start Date" type="date" value={calcForm.start_date}
                                onChange={e => setCalcForm(prev => ({ ...prev, start_date: e.target.value }))}
                                size="small" sx={{ width: 170 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            />
                            <TextField label="End Date" type="date" value={calcForm.end_date}
                                onChange={e => setCalcForm(prev => ({ ...prev, end_date: e.target.value }))}
                                size="small" sx={{ width: 170 }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                            />
                            <Button variant="contained" startIcon={calculating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CalculateIcon />}
                                onClick={handleCalculate}
                                disabled={calculating || !calcForm.city || !calcForm.state || !calcForm.start_date || !calcForm.end_date}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, height: 40 }}
                            >
                                Calculate
                            </Button>
                        </Box>

                        {calcResult && (
                            <Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                                    <Paper sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Days</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 800, color: '#1e293b', mt: 0.5 }}>{calcResult.days || calcResult.total_days || '-'}</Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Lodging</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 800, color: '#3b82f6', mt: 0.5 }}>{formatCurrency(calcResult.lodging_total || calcResult.total_lodging)}</Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Meals & Incidentals</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 800, color: '#f59e0b', mt: 0.5 }}>{formatCurrency((calcResult.meals_total || calcResult.total_meals || 0) + (calcResult.incidentals_total || calcResult.total_incidentals || 0))}</Typography>
                                    </Paper>
                                    <Paper sx={{ p: 2, borderRadius: '14px', border: '2px solid #22c55e', boxShadow: 'none', bgcolor: '#f0fdf4' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#166534', textTransform: 'uppercase' }}>Total Per Diem</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 800, color: '#166534', mt: 0.5 }}>{formatCurrency(calcResult.total || calcResult.grand_total)}</Typography>
                                    </Paper>
                                </Box>

                                {calcResult.daily_breakdown && calcResult.daily_breakdown.length > 0 && (
                                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                        {['Date', 'Lodging', 'Meals', 'Incidentals', 'Total', 'Notes'].map(h => (
                                                            <TableCell key={h} sx={{
                                                                fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                                                                textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                                                                textAlign: ['Lodging', 'Meals', 'Incidentals', 'Total'].includes(h) ? 'right' : 'left',
                                                            }}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {calcResult.daily_breakdown.map((day, idx) => (
                                                        <TableRow key={idx} hover>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatDate(day.date)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(day.lodging)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(day.meals)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b', textAlign: 'right' }}>{formatCurrency(day.incidentals)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', textAlign: 'right' }}>{formatCurrency(day.total)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                                                                {day.is_first_day ? 'First day' : day.is_last_day ? 'Last day' : ''}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Card>
                                )}
                            </Box>
                        )}
                    </Card>
                )}
            </Box>

            {/* Rate Create/Edit Dialog */}
            <Dialog open={rateDialog.open} onClose={() => setRateDialog({ open: false, editing: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {rateDialog.editing ? 'Edit Per Diem Rate' : 'Add Per Diem Rate'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="City" value={rateForm.city}
                            onChange={e => setRateForm(prev => ({ ...prev, city: e.target.value }))}
                            size="small" sx={{ flex: 2 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                        <TextField label="State" value={rateForm.state}
                            onChange={e => setRateForm(prev => ({ ...prev, state: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Box>
                    <TextField label="Lodging Rate" value={rateForm.lodging_rate}
                        onChange={e => setRateForm(prev => ({ ...prev, lodging_rate: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { fontFamily, borderRadius: '10px' }, inputProps: { min: 0, step: '0.01' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Meals Rate" value={rateForm.meals_rate}
                        onChange={e => setRateForm(prev => ({ ...prev, meals_rate: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { fontFamily, borderRadius: '10px' }, inputProps: { min: 0, step: '0.01' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Incidentals Rate" value={rateForm.incidentals_rate}
                        onChange={e => setRateForm(prev => ({ ...prev, incidentals_rate: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { fontFamily, borderRadius: '10px' }, inputProps: { min: 0, step: '0.01' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRateDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveRate}
                        disabled={rateSaving || !rateForm.city || !rateForm.state}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {rateSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : rateDialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Claim Create Dialog */}
            <Dialog open={claimDialog.open} onClose={() => setClaimDialog({ open: false })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>New Per Diem Claim</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Travel Date" type="date" value={claimForm.travel_date}
                        onChange={e => setClaimForm(prev => ({ ...prev, travel_date: e.target.value }))}
                        fullWidth size="small"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="City" value={claimForm.city}
                            onChange={e => setClaimForm(prev => ({ ...prev, city: e.target.value }))}
                            size="small" sx={{ flex: 2 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                        <TextField label="State" value={claimForm.state}
                            onChange={e => setClaimForm(prev => ({ ...prev, state: e.target.value }))}
                            size="small" sx={{ flex: 1 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={claimForm.is_first_day} onChange={e => setClaimForm(prev => ({ ...prev, is_first_day: e.target.checked }))}
                                sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                            />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>First day of travel</Typography>}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={claimForm.is_last_day} onChange={e => setClaimForm(prev => ({ ...prev, is_last_day: e.target.checked }))}
                                sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                            />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Last day of travel</Typography>}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setClaimDialog({ open: false })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveClaim}
                        disabled={claimSaving || !claimForm.travel_date || !claimForm.city || !claimForm.state}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {claimSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Claim'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
